import { z } from 'zod';
import { Api } from 'further-types/companies';

export const validateCompanyHouseLength = (value?: string) => {
  if (value && !/^[a-zA-Z0-9]{8,12}$/.test(value)) {
    return false;
  }
  return true;
};

// Shared base fields
const baseFormFields = {
  firmId: z.string().min(1, 'Firm ID is required.'),
  companyLogo: z.string().optional().nullable(),
  previewCompanyLogo: z.string().optional().nullable(),
  isNewCompany: z.boolean(),
  isNewShareClass: z.boolean().optional(),
  investeeId: z.string().optional(),
  shareClassId: z.string().optional(),
};

// Schema for creating a new company
const newCompanyFormSchema = z.object({
  ...baseFormFields,
  isNewCompany: z.boolean(),
  legalName: z.string().min(1, 'Legal name is required.'),
  tradingName: z.string().min(1, 'Trading name is required.'),
  url: z.string().optional(),
  twitterUrl: z.string().optional(),
  linkedinUrl: z.string().optional(),
  companiesHouseNumber: z
    .string()
    .optional()
    .refine(validateCompanyHouseLength, {
      message: 'Companies house number must be between 8 and 12 characters.',
    }),
  description: z.string().min(1, 'Description is required.'),
  shareClass: z.string().min(1, 'Share class is required.'),
  nominalShareValue: z.string().optional(),
  sector: z
    .string()
    .refine(
      (value) =>
        value !== '' && Object.values(Api.Sector).includes(value as Api.Sector),
      { message: 'Please select a valid sector.' },
    ),
  stage: z
    .string()
    .refine(
      (value) =>
        value !== '' && Object.values(Api.Stage).includes(value as Api.Stage),
      { message: 'Please select a valid stage.' },
    ),
});

// Schema for creating a new share class for an existing company
const newShareClassFormSchema = z
  .object({
    ...baseFormFields,
    isNewCompany: z.boolean(),
    isNewShareClass: z.boolean(),
    investeeId: z.string().optional(),
    shareClass: z.string().optional(),
    nominalShareValue: z.string().optional(),
    // All company fields optional
    legalName: z.string().optional(),
    tradingName: z.string().optional(),
    url: z.string().optional(),
    twitterUrl: z.string().optional(),
    linkedinUrl: z.string().optional(),
    companiesHouseNumber: z.string().optional(),
    description: z.string().optional(),
    sector: z.string().optional(),
    stage: z.string().optional(),
  })
  .refine(
    (data) => {
      // If isNewShareClass is true, shareClass is required
      if (data.isNewShareClass) {
        return data.shareClass && data.shareClass.trim().length > 0;
      }
      // If isNewShareClass is false, shareClass is optional
      return true;
    },
    {
      message: 'Share class is required.',
      path: ['shareClass'],
    },
  );

export function getInvesteeFormSchema(isNewCompany: boolean) {
  return isNewCompany ? newCompanyFormSchema : newShareClassFormSchema;
}

export const createInvesteeServiceInputSchema = z.object({
  legalName: z.string().min(1, 'Legal name is required.'),
  tradingName: z.string().min(1, 'Trading name is required.'),
  url: z.string().optional(),
  twitterUrl: z.string().optional(),
  linkedinUrl: z.string().optional(),
  companiesHouseNumber: z
    .string()
    .optional()
    .refine(validateCompanyHouseLength, {
      message:
        'Companies house number must be between 8 and 12 alphanumeric characters.',
    }),
  description: z.string().min(1, 'Description is required.'),
  shareClass: z.string().min(1, 'Share class is required.'),
  nominalShareValue: z.string().optional(),
  sector: z
    .string()
    .refine(
      (value) =>
        value === '' || Object.values(Api.Sector).includes(value as Api.Sector),
      {
        message: 'Please select a valid sector.',
      },
    )
    .refine((value) => value !== '', {
      message: 'Please select a sector.',
    }),
  stage: z
    .string()
    .refine(
      (value) =>
        value === '' || Object.values(Api.Stage).includes(value as Api.Stage),
      {
        message: 'Please select a valid stage.',
      },
    )
    .refine((value) => value !== '', {
      message: 'Please select a stage.',
    }),
  fundId: z.string().optional(),
  firmId: z.string().min(1, 'Firm ID is required.'),
  companyLogo: z.string().optional().nullable(),
});

export const listUniqueInvesteesInputSchema = z.object({
  firmId: z.string(),
  currentShareholdingsOnly: z.boolean().optional(),
});

export const listShareClassesInputSchema = z.object({
  investeeId: z.string(),
});

export const createShareClassInputSchema = z.object({
  investeeId: z.string().min(1, 'Investee ID is required.'),
  shareClass: z.string().min(1, 'Share class name is required.'),
  nominalShareValue: z.string().optional(),
});

export type NewCompanyFormValues = z.infer<typeof newCompanyFormSchema>;
export type NewShareClassFormValues = z.infer<typeof newShareClassFormSchema>;
